/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { createContext, useContext, useMemo, type ReactNode } from "react";

export type UiOptionsValue = {
  /** Options for enabling and disabling experimental or testing features */
  experimental?: {
    /**
     * If enabled, the component Alert with severity equal to info will have black background color.
     * The option is experimental until the task is completed. https://st.yandex-team.ru/SUPERWEB-1365
     */
    alertInfoBlackBackground?: boolean;
    /**
     * If enabled, the field components for enter date or time use custom field layout.
     */
    dateFieldCustomFieldLayout?: boolean;
    /**
     * If enabled, popover element e.g. listbox in Select or menu in MenuButton
     * will be rendered inside AppContainer instead of document.body.
     */
    renderPopoversInsideAppContainer?: boolean;

    /**
     * Enable new scroll behavior for ListDetails component.
     */
    enableNewScrollBehaviorForListDetails?: boolean;

    /**
     * Enable delay show calendar dialog in mobile
     */
    enableDelayShowCalendarDialogInMobile?: boolean;

    /**
     * If enabled, TagField input will be hidden when not focused,
     * and visible when focused. This prevents empty line below tags.
     */
    tagFieldHideInputWhenNofocus?: boolean;
  };
};

const defaultValue: UiOptionsValue = {
  experimental: {
    dateFieldCustomFieldLayout: true,
    renderPopoversInsideAppContainer: true,
  },
};

const context = createContext<UiOptionsValue>(defaultValue);

/**
 * Allows to set options for all components from `@superweb/ui`
 */
export const UiOptions = ({
  value,
  children,
}: {
  value: UiOptionsValue;
  children: ReactNode;
}) => {
  const nextValue = useMemo(() => {
    return {
      ...defaultValue,
      ...value,
      experimental: {
        ...defaultValue.experimental,
        ...value.experimental,
      },
    };
  }, [value]);

  return <context.Provider value={nextValue}>{children}</context.Provider>;
};
/**
 * @internal
 * Hook for get options
 */
export const useUiOptions = () => {
  return useContext(context);
};
